import React from "react";
import { useFormikContext } from "formik";
import baseStyles from "../../style.module.scss";
import styles from "./style.module.scss";
import ReviewHeader from "../common/ReviewHeader";
import TieredRate from "./TieredRate";
import FlatRate from "./FlatRate";
import ICPlus from "./ICPlus";
import Surcharge from "./Surcharge";
import DualPricing from "./DualPricing";
import KurvPricing from "./KurvPricing";
import Ach from "./Ach";
import getPricingDataById from "./utils/get-pricing-data-by-id";
import formatCurrency from "./utils/format-currency";
import formatPercentage from "./utils/format-percentage";
import useSalesFormStore from "../../../../../../../../stores/sales-form";

/**
 * @typedef {Object} PricingValues
 * @property {string} pricing_model - The pricing model type
 * @property {number} [preferred_rate] - Preferred rate for dual pricing
 * @property {number} [monthly_fee] - Monthly fee for dual pricing
 * @property {number} [flat_rate] - Flat rate for flat rate pricing
 * @property {number} [ic_plus_rate] - IC+ rate for IC+ pricing
 * @property {number} [rate] - Rate for surcharge pricing
 * @property {number} [qualified_rate] - Qualified rate for tiered pricing
 */

/**
 * @typedef {Object} PricingProps
 * @property {Function} onEdit - Callback when edit button is clicked
 * @property {boolean} isOpen - Whether the section is expanded
 * @property {Function} onToggle - Callback when section is toggled
 */

/**
 * Pricing component for displaying different pricing models
 * @param {PricingProps} props - Component props
 * @returns {React.ReactElement} Pricing component
 */
const Pricing = ({ onEdit, isOpen, onToggle }) => {
  const { values } = useFormikContext();
  const applicationData = useSalesFormStore((state) => state.applicationData);

  const SectionHeader = ({ title }) => (
    <div className={baseStyles.sectionHeader}>
      <span className={baseStyles.sectionTitle}>{title}</span>
      <div className={baseStyles.sectionLine} />
    </div>
  );

  const getPricingModelLabel = (model) => {
    const models = {
      dual_pricing: "Dual Pricing",
      surcharge: "Surcharge",
      flat_rate: "Flat Rate",
      ic_plus: "IC+",
      tiered: "Tiered Rate",
      kurv_pricing: "Kurv Pricing",
      ach: "ACH",
    };
    return models[model] || model;
  };

  const qualifiedType = applicationData?.qualifiedtype;
  const hasCardPricing = Boolean(qualifiedType) && qualifiedType !== "ach";
  const hasAchPricing =
    qualifiedType === "ach" || Boolean(applicationData?.is_ach);

  const renderPricingContent = () => {
    switch (applicationData.qualifiedtype) {
      case "tiered":
        return <TieredRate />;
      case "flat_rate":
        return <FlatRate />;
      case "ic_plus":
        return <ICPlus />;
      case "surcharge":
        return <Surcharge />;
      case "dual_pricing":
        return <DualPricing />;
      case "kurv_pricing":
        return <KurvPricing />;
      default:
        return null;
    }
  };

  return (
    <div className={baseStyles.reviewSectionContainer}>
      <ReviewHeader
        title="Pricing"
        isOpen={isOpen}
        onToggle={onToggle}
        onEdit={onEdit}
      />

      {isOpen && (
        <div className={baseStyles.reviewContent}>
          <div className={baseStyles.section}>
            <div className={styles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Pricing Model</div>
                <div className={baseStyles.detailValue}>
                  {getPricingModelLabel(qualifiedType)}
                </div>
              </div>

              {applicationData?.qualifiedtype === "tiered" &&
                applicationData?.tiered_type && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>
                      Tiered Pricing Type
                    </div>
                    <div
                      className={styles.detailValue}
                      style={{ textTransform: "capitalize" }}
                    >
                      {applicationData.tiered_type}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {renderPricingContent()}

          {/* Pin-Based Debit Section */}
          {hasCardPricing && qualifiedType !== "dual_pricing" && (
            <div className={baseStyles.section}>
              <SectionHeader title="PIN-BASED DEBIT" />
              <div className={styles.detailsGrid}>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Network Fee</div>
                  <div className={baseStyles.detailValue}>
                    {applicationData?.billing?.network_fee == "yes"
                      ? "Yes"
                      : "No"}
                  </div>
                </div>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Debit rate</div>
                  <div className={baseStyles.detailValue}>
                    {formatPercentage(
                      getPricingDataById(5, applicationData?.prices)?.percent,
                    )}
                  </div>
                </div>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Txn rate</div>
                  <div className={baseStyles.detailValue}>
                    {formatCurrency(
                      getPricingDataById(5, applicationData?.prices)?.dollars,
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {applicationData?.qualifiedtype === "surcharge" && (
            <div className={baseStyles.section}>
              <SectionHeader title="Surcharge Debit Structure" />
              <div className={styles.detailsGrid}>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Debit Structure</div>
                  <div className={baseStyles.detailValue}>IC Plus</div>
                </div>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>IC+ Rate</div>
                  <div className={baseStyles.detailValue}>
                    {formatPercentage(
                      getPricingDataById(6, applicationData?.prices)?.percent,
                    )}
                  </div>
                </div>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>IC+ Txn Fee</div>
                  <div className={baseStyles.detailValue}>
                    {formatCurrency(
                      getPricingDataById(6, applicationData?.prices)?.dollars,
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Default Fees Section */}
          {hasCardPricing && (
            <div className={baseStyles.section}>
              <SectionHeader title="DEFAULT FEES" />
              <div className={styles.detailsGrid}>
                {[
                  { key: 28, label: "Setup Fee" },
                  { key: 19, label: "Monthly Access Fee" },
                  { key: 42, label: "$100K Data Breach" },
                  { key: 18, label: "Monthly Minimum" },
                  { key: 43, label: "Semi-Annual Tech Fee" },
                  { key: 12, label: "Voice ARU" },
                  { key: 14, label: "Batch Fee" },
                  { key: 22, label: "Returned ACH Item" },
                  { key: 13, label: "AVS Fee" },
                  { key: 11, label: "3rd Party Auth/Txn" },
                  { key: 46, label: "ETF Fee" },
                  { key: 20, label: "Chargeback Fee" },
                  { key: 47, label: "Representment Fee" },
                  { key: 48, label: "Pre-Dispute Fee" },
                  { key: 61, label: "Card Brand Fee" },
                  { key: 62, label: "PCI Non-Compliance Fee" },
                  { key: 21, label: "Retrieval Fee" },
                  { key: null, label: null },
                ].map((fee, index) => (
                  <div key={index} className={baseStyles.detailItem}>
                    {fee.label && fee.key && (
                      <>
                        <div className={baseStyles.detailLabel}>
                          {fee.label}
                        </div>
                        <div className={baseStyles.detailValue}>
                          {formatCurrency(
                            getPricingDataById(fee.key, applicationData?.prices)
                              ?.dollars,
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasAchPricing && hasCardPricing && (
            <div className={baseStyles.section}>
              <div className={styles.detailsGrid}>
                <div className={baseStyles.detailItem}>
                  <div className={baseStyles.detailLabel}>Pricing Model</div>
                  <div className={baseStyles.detailValue}>ACH</div>
                </div>
              </div>
            </div>
          )}

          {/* ACH Section */}
          {hasAchPricing && <Ach />}

          {/* VAR Sheet Section */}
          {values.reprogram_hardware &&
            values.reprogram_hardware.length > 0 && (
              <div className={baseStyles.section}>
                <SectionHeader title="VAR SHEET" />
                <div className={styles.detailsGrid}>
                  {values.reprogram_hardware.map((item, index) => (
                    <div key={index} className={baseStyles.detailItem}>
                      <div className={baseStyles.detailLabel}>
                        Reprogram equipment {index + 1}
                      </div>
                      <div className={baseStyles.detailValue}>{item.model}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default Pricing;
